import { useState } from "react";
import { BedDouble, Eye, Check, Phone } from "lucide-react";
import { useIsPro } from "../../app/bandContext.js";
import { pushToast } from "../../app/toasts.js";
import { useMyBunk } from "../../app/hooks/useBuses.js";
import { BusStockPanel } from "./BusStockPanel.js";
import styles from "./MyBunkCard.module.scss";

/**
 * ARTIST CNTRL PRO — the "My Bus" card / the My Bunk surface (ART-147 §7.5, widened by ART-148). ONE hook
 * (`useMyBunk`) behind every place a person's own bunk shows — this card on Day of Show and Show Logistics, plus
 * the Home row and the desktop dashboard rail. It gates on `isPro` and NOTHING else: a bunk is personal, so
 * whoever is bunked sees their own whatever seat they hold (unlike the fleet chart, which is AV-or-TM). The
 * codes ride along because you are entitled to your OWN coach's logistics — door/bay render blurred-until-tapped,
 * and the Wi-Fi password is never printed (copy-to-clipboard only), the §7.5 security posture.
 *
 * `emptyMode` is the surface split. `hide` (Home, dashboard) renders ONLY a real bunk — an empty "no bus" row on
 * every non-touring day is noise. `note` (Day of Show, Show Logistics) also says something useful to an
 * UNBUNKED rider (PO, ART-148): on a band with exactly one coach the server hands that coach over, so a tech who
 * joined mid-tour still gets the driver and the door code under a "Not bunked" hero; with two or more coaches
 * nothing says which is theirs, so the card says to ask the TM rather than guessing a coach's codes. A band with
 * no coach at all renders nothing anywhere, and so does a fleet EDITOR with no coach of their own — they manage
 * the whole fleet in Buses, where an "ask your TM" line would be talking to the TM.
 */
export function MyBunkCard({ emptyMode = "note" }: { emptyMode?: "hide" | "note" }) {
  const isPro = useIsPro();
  const q = useMyBunk(isPro);

  if (!isPro || q.isLoading || q.isError || !q.data) return null;
  const { hasBunk, bunk, coaches, canEditFleet } = q.data;

  if (hasBunk && bunk) {
    return (
      <div className={styles.card}>
        <div className={styles.head}><BedDouble size={15} /> Your bunk</div>
        {/* The bus is the header and the bunk is spelled out under it (Ryan, ART-148): the P/F/T shorthand
            beside the written-out position said the same thing twice. */}
        <div className={styles.big}>
          <span className={styles.bus}>{bunk.vehicleName}</span>
          <span className={styles.long}>{bunk.long}</span>
        </div>
        <CoachFacts coach={bunk} />
        {/* The crew panel for THIS coach (§7.6) — add/see restock asks; author-only retract. */}
        <BusStockPanel vehicleId={bunk.vehicleId} compact />
      </div>
    );
  }

  if (emptyMode === "hide" || coaches === 0) return null;

  if (bunk) {
    // The band's ONLY coach, and no sleeper on it for you yet — the bus is still yours to get on.
    return (
      <div className={styles.card}>
        <div className={styles.head}><BedDouble size={15} /> Your bus</div>
        <div className={styles.big}>
          <span className={styles.bus}>{bunk.vehicleName}</span>
          <span className={styles.long}>Not bunked · no sleeper assigned to you yet</span>
        </div>
        <CoachFacts coach={bunk} />
        <BusStockPanel vehicleId={bunk.vehicleId} compact />
      </div>
    );
  }

  if (canEditFleet) return null;
  return (
    <div className={styles.card}>
      <div className={styles.head}><BedDouble size={15} /> Your bus</div>
      <p className={styles.empty}>
        You're not on a coach yet. Ask your tour manager which bus is yours — your bunk and the bus codes show up
        here once they put you on one.
      </p>
    </div>
  );
}

/** The bus-level facts every rider needs, for one coach: driver (one-tap call), door + bay behind the two-tap
 *  gate, the lock note, Wi-Fi network and a copy-only password. Shared by this card and the AV/TM fleet pager,
 *  so the security posture has one home. Rows we hold nothing for are omitted — a short list, not a form. */
export interface CoachFactsData {
  driver: string | null; driverPhone: string | null; door: string | null; bay: string | null;
  wifi: string | null; wifiPassword: string | null; lockNote: string | null;
}

export function CoachFacts({ coach }: { coach: CoachFactsData }) {
  return (
    <div className={styles.rows}>
      <Row k="Driver" v={coach.driver} />
      <DriverPhoneRow phone={coach.driverPhone} />
      <CodesGate door={coach.door} bay={coach.bay} />
      <Row k="Lock note" v={coach.lockNote} />
      <Row k="Wi-Fi" v={coach.wifi} mono />
      <Row k="Wi-Fi password" v={coach.wifiPassword} copy />
    </div>
  );
}

function Row({ k, v, mono, copy }: { k: string; v?: string | null; mono?: boolean; copy?: boolean }) {
  if (!v) return null;
  return (
    <div className={styles.row}>
      <span className={styles.k}>{k}</span>
      {copy ? (
        <button
          type="button"
          className={`${styles.v} ${styles.copy}`}
          onClick={() => { void navigator.clipboard?.writeText(v); pushToast("Copied", "success"); }}
        >Copy</button>
      ) : (
        <span className={`${styles.v} ${mono ? styles.mono : ""}`}>{v}</span>
      )}
    </div>
  );
}

/** The driver's number as a one-tap `tel:` call — a green pill, the only success-coloured control on the card
 *  (ART-174 §My Bus). */
function DriverPhoneRow({ phone }: { phone?: string | null }) {
  if (!phone) return null;
  return (
    <div className={styles.row}>
      <span className={styles.k}>Driver phone</span>
      <a href={`tel:${phone.replace(/[^\d+]/g, "")}`} className={styles.telPill}>
        <Phone size={12} /> {phone}
      </a>
    </div>
  );
}

/** Door + bay behind ONE two-tap gate (ART-174 §My Bus): first tap reveals BOTH (one shown flag covers both),
 *  second tap copies both and shows a green check. The codes are never rendered until revealed. */
function CodesGate({ door, bay }: { door?: string | null; bay?: string | null }) {
  const [shown, setShown] = useState(false);
  const [copied, setCopied] = useState(false);
  const parts = [door ? `Door ${door}` : "", bay ? `Bay ${bay}` : ""].filter(Boolean);
  if (parts.length === 0) return null;
  const text = parts.join(" · ");
  const onClick = () => {
    if (!shown) {
      setShown(true);
      return;
    }
    void navigator.clipboard?.writeText(text);
    setCopied(true);
    pushToast("Copied", "success");
    window.setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div className={styles.row}>
      <span className={styles.k}>Codes</span>
      <button
        type="button"
        className={`${styles.v} ${styles.mono} ${styles.codeBtn}`}
        aria-label={shown ? "Copy door and bay codes" : "Reveal door and bay codes"}
        onClick={onClick}
      >
        {shown ? text : "•••• ••••"}
        {copied ? <Check size={12} className={styles.check} /> : <Eye size={12} className={styles.eye} />}
      </button>
    </div>
  );
}
